import { hmac } from '@noble/hashes/hmac.js'
import { sha256 } from '@noble/hashes/sha2.js'
import { base64url } from '@scure/base'
import { hexToBytes } from '@noble/hashes/utils.js'
import client from '@/services/client.service'
import { currentPubkeyAtom } from './session'
import { atom, getDefaultStore } from 'jotai'

const store = getDefaultStore()

const currentPubkeyBytesAtom = atom((get) => {
  const currentPubkey = get(currentPubkeyAtom)
  if (currentPubkey) return hexToBytes(currentPubkey)
})

const pubkeySecretPromise = atom(async () => {
  if (!window.hallway.universe.imgproxyUrl) return
  if (window.hallway.universe?.imgproxyKey) return

  const cleanBase = window.hallway.universe.imgproxyUrl.replace(/\/+$/, '')
  const secretUrl = `${cleanBase}/secret`
  try {
    const auth = await client.signHttpAuth(secretUrl, 'GET')
    const res = await fetch(secretUrl, { headers: { Authorization: auth } })
    const secretHex = await res.text()
    return hexToBytes(secretHex.trim())
  } catch {
    return undefined
  }
})

const FAILURE_THRESHOLD = 4

const failureCountAtom = atom(0)
const recheckVersionAtom = atom((get) => Math.floor(get(failureCountAtom) / FAILURE_THRESHOLD))

const initialHealthCheckAtom = atom(async (get) => {
  get(recheckVersionAtom)

  const { imgproxyUrl } = window.hallway?.universe || {}
  if (!imgproxyUrl) return false

  const targetURL = `${window.location.origin}/favicon.ico`
  let proxyURL: string
  try {
    proxyURL = await buildProxyUrl(targetURL, '')
  } catch {
    return false
  }
  if (!proxyURL || proxyURL === targetURL) return false

  return new Promise<boolean>((resolve) => {
    const img = new Image()
    const timer = setTimeout(() => resolve(false), 5000)
    img.onload = () => {
      clearTimeout(timer)
      resolve(true)
    }
    img.onerror = () => {
      clearTimeout(timer)
      resolve(false)
    }
    img.src = proxyURL
  })
})

export const imgproxyHealthyAtom = atom(async (get) => {
  return get(initialHealthCheckAtom)
})

export function reportImgproxyFailure() {
  store.set(failureCountAtom, (c) => c + 1)
}

async function buildProxyUrl(targetURL: string, modifiers = ''): Promise<string> {
  const { imgproxyUrl, imgproxyKey, imgproxySalt } = window.hallway?.universe || {}
  if (!imgproxyUrl) return targetURL

  const cleanBase = imgproxyUrl.replace(/\/+$/, '')
  const encoded = targetURL
    .replace('/', '%2F')
    .replace(/%/g, '%25')
    .replace(/\?/g, '%3F')
    .replace(/@/g, '%40')
  const modifiersPath = modifiers ? `/${modifiers}` : ''

  if (imgproxyKey && imgproxySalt) {
    const path = `${modifiersPath}/plain/${encoded}@avif`
    const keyBytes = hexToBytes(imgproxyKey)
    const saltedPathBytes = new TextEncoder().encode(imgproxySalt + '/' + path)

    const digest = hmac(sha256, keyBytes, saltedPathBytes)
    const signature = base64url.encode(digest)
    return `${cleanBase}/${signature}${path}`
  }

  const pubkeyBytes = store.get(currentPubkeyBytesAtom)
  const pubkeySecret = await store.get(pubkeySecretPromise)
  if (pubkeySecret && pubkeyBytes) {
    const mac = hmac(
      sha256,
      pubkeySecret,
      new TextEncoder().encode(`${modifiersPath}/plain/${targetURL}@avif`)
    )
    const tokenBytes = new Uint8Array(48)
    tokenBytes.set(mac.slice(0, 16), 0)
    tokenBytes.set(pubkeyBytes!, 16)
    const token = base64url.encode(tokenBytes)

    return `${cleanBase}/${token}${modifiersPath}/plain/${encoded}@avif`
  }

  return `${cleanBase}/insecure/plain/${encoded}@avif`
}

export async function proxyMediaUrl(targetURL: string, modifiers = ''): Promise<string> {
  if (!targetURL.startsWith('http')) return targetURL
  const { imgproxyUrl } = window.hallway?.universe || {}
  if (!imgproxyUrl) return targetURL

  const healthy = await store.get(imgproxyHealthyAtom)
  if (!healthy) return targetURL

  return buildProxyUrl(targetURL, modifiers)
}
